/**
 * ¿Ayuda José de Faktelio? — `OD-0058-ENMIENDA-01` E-07 y `ADR-0024` D-06.
 *
 * Solo lectura. La API suma contadores diarios SIN ids y devuelve las cinco
 * respuestas ya calculadas; aquí solo se pintan. No hay ninguna empresa ni
 * ningún usuario que enseñar, porque no se guardan.
 */
import { useCallback, useEffect, useState } from "react";
import { api, type GanadasPerdidas, type InformeJose } from "../api/cliente";
import { t } from "../textos";

function FilaCierre({ grupo, gp }: { grupo: string; gp: GanadasPerdidas }) {
  return (
    <tr>
      <td>{grupo}</td>
      <td>{gp.ganadas}</td>
      <td>{gp.perdidas}</td>
      <td>{t.jose.porcentaje(gp.porcentajeGanadas)}</td>
    </tr>
  );
}

export function MetricasJose() {
  const [estado, setEstado] = useState<"cargando" | "ok" | "no-encontrado" | "sin-mfa" | "invalido" | "error">("cargando");
  const [informe, setInforme] = useState<InformeJose | null>(null);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const cargar = useCallback(async (d: string | null, h: string | null): Promise<void> => {
    setEstado("cargando");
    const r = await api.metricasJose(d, h);
    if (r.ok) {
      setInforme(r.datos);
      setDesde(r.datos.desde);
      setHasta(r.datos.hasta);
      setEstado("ok");
      return;
    }
    if (r.estado === 404 || r.estado === 401) setEstado("no-encontrado");
    else if (r.estado === 403) setEstado("sin-mfa");
    else if (r.estado === 400 || r.estado === 422) setEstado("invalido");
    else setEstado("error");
  }, []);

  useEffect(() => {
    void cargar(null, null);
  }, [cargar]);

  if (estado === "no-encontrado") return <p>{t.comun.noEncontrado}</p>;
  if (estado === "sin-mfa") return <p className="aviso" data-tono="aviso">{t.comun.sinMfa}</p>;

  return (
    <>
      <h1>{t.jose.titulo}</h1>
      <p className="suave">{t.jose.explicacion}</p>
      <form
        className="filtros"
        onSubmit={(e) => {
          e.preventDefault();
          void cargar(desde === "" ? null : desde, hasta === "" ? null : hasta);
        }}
      >
        <label>
          {t.jose.desde} <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label>
          {t.jose.hasta} <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </label>
        <button type="submit">{t.jose.ver}</button>
      </form>

      {estado === "cargando" ? <p className="suave">{t.comun.cargando}</p> : null}
      {estado === "invalido" ? <p className="aviso" data-tono="error">{t.jose.periodoInvalido}</p> : null}
      {estado === "error" ? <p className="aviso" data-tono="error">{t.comun.error}</p> : null}

      {estado === "ok" && informe !== null ? (
        <>
          <p className="suave">{t.jose.periodo(informe.desde, informe.hasta)}</p>

          <h2>{t.jose.cualificacion.titulo}</h2>
          <p>
            {t.jose.cualificacion.empezadas(informe.cualificacion.empezadas)}
            <br />
            {t.jose.cualificacion.completadas(informe.cualificacion.completadas)}
          </p>

          <h2>{t.jose.pasos.titulo}</h2>
          <p>
            <strong>{t.jose.pasos.total(informe.pasos.apuntados, informe.pasos.hechos)}</strong> ({t.jose.porcentaje(informe.pasos.porcentajeHechos)})
          </p>
          {informe.pasos.porPaso.length === 0 ? (
            <p className="suave">{t.jose.pasos.vacio}</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t.jose.pasos.columnas.paso}</th>
                  <th>{t.jose.pasos.columnas.apuntados}</th>
                  <th>{t.jose.pasos.columnas.hechos}</th>
                  <th>{t.jose.pasos.columnas.utiles}</th>
                  <th>{t.jose.pasos.columnas.noUtiles}</th>
                </tr>
              </thead>
              <tbody>
                {informe.pasos.porPaso.map((p) => (
                  <tr key={p.paso}>
                    <td>
                      {t.jose.nombre(p.paso)}
                      <div className="suave">{p.paso}</div>
                    </td>
                    <td>{p.apuntados}</td>
                    <td>{p.hechos}</td>
                    <td>{p.utiles}</td>
                    <td>{p.noUtiles}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h2>{t.jose.cierres.titulo}</h2>
          <table>
            <thead>
              <tr>
                <th>{t.jose.cierres.columnas.grupo}</th>
                <th>{t.jose.cierres.columnas.ganadas}</th>
                <th>{t.jose.cierres.columnas.perdidas}</th>
                <th>{t.jose.cierres.columnas.porcentaje}</th>
              </tr>
            </thead>
            <tbody>
              <FilaCierre grupo={t.jose.cierres.sinCualificar} gp={informe.cierres.sinCualificar} />
              <FilaCierre grupo={t.jose.cierres.cualificadas} gp={informe.cierres.cualificadas} />
              <FilaCierre grupo={t.jose.cierres.aMano} gp={informe.cierres.aMano} />
            </tbody>
          </table>

          <h2>{t.jose.acierto.titulo}</h2>
          <p className="suave">{t.jose.acierto.detalle}</p>
          {informe.acierto.length === 0 ? (
            <p className="suave">{t.jose.acierto.vacio}</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t.jose.acierto.columnas.porcentaje}</th>
                  <th>{t.jose.acierto.columnas.ganadas}</th>
                  <th>{t.jose.acierto.columnas.perdidas}</th>
                  <th>{t.jose.acierto.columnas.real}</th>
                </tr>
              </thead>
              <tbody>
                {informe.acierto.map((a) => (
                  <tr key={a.porcentaje}>
                    <td>{t.jose.porcentaje(a.porcentaje)}</td>
                    <td>{a.ganadas}</td>
                    <td>{a.perdidas}</td>
                    <td>{t.jose.porcentaje(a.porcentajeGanadas)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h2>{t.jose.consejos.titulo}</h2>
          <p className="suave">{t.jose.consejos.detalle}</p>
          {informe.consejos.length === 0 ? (
            <p className="suave">{t.jose.consejos.vacio}</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t.jose.consejos.columnas.consejo}</th>
                  <th>{t.jose.consejos.columnas.utiles}</th>
                  <th>{t.jose.consejos.columnas.noUtiles}</th>
                  <th>{t.jose.consejos.columnas.otro}</th>
                </tr>
              </thead>
              <tbody>
                {informe.consejos.map((c) => (
                  <tr key={c.consejo}>
                    <td>
                      {t.jose.nombre(c.consejo)}
                      <div className="suave">{c.consejo}</div>
                    </td>
                    <td>{c.utiles}</td>
                    <td>{c.noUtiles}</td>
                    <td>{c.otro}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h2>{t.jose.recomendaciones.titulo}</h2>
          <p>
            {(["1", "2", "3"] as const).map((n) => (
              <span key={n}>
                {t.jose.recomendaciones.puesto(n, informe.recomendaciones[n])}
                <br />
              </span>
            ))}
          </p>
        </>
      ) : null}
    </>
  );
}
